import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  defaultMeasureControlOptions,
  MaplibreMeasureControl,
  MaplibreTerradrawControl,
} from "@watergis/maplibre-gl-terradraw";
import "@watergis/maplibre-gl-terradraw/dist/maplibre-gl-terradraw.css";
import {
  checkColor,
  checkOpacity,
  checkWidth,
  createFeaturePropertyKey,
  Func_chk_PLATEAU_Building_Visibility,
  Func_chk_plateau_dem,
} from "./Util";
import proj4 from "proj4";
import { PosInMap } from "../lib/autocontent";
import { Protocol } from "pmtiles";
import {
  Arr_NoDispMap,
  Arr_NoDispNiniBlocks,
  Arr_NoDispNiniFiles,
  BizFontName,
  BizFontNameNINI,
  bizyoffset,
  F_Hude_fill_opacity,
  HUDE_COLORS,
  mojcopyright,
  StyleURL,
  StyleURL_Gray,
} from "../constants/Constant";
import rightLeftIcon from "../assets/right-left.svg";
import paletteIcon from "../assets/palette.svg";
import downloadIcon from "../assets/download.svg";
import uploadIcon from "../assets/upload.svg";
import {
  TerraDrawLineStringMode,
  TerraDrawPolygonMode,
  TerraDrawSelectMode,
} from "terra-draw";
import { loadState } from "./SaveStateUtil";

const IS_ADD_MEASURE = true;
let hoveredStateIdArray = [];
let niniHoveredStateIdArray = [];
// 計測・図形機能の利用状態
let isDrawCtlUsing = false;
// 購入履歴の表示フラグ
let isOredrHistoryView = loadState("isReflectHistoryOnMap") ?? false;
// 購入履歴マップ
let orderHistoryMap = new Map();
// 選択された地物情報を更新するためのセッター関数
let _addSelectedFeatures = null;
let _deleteSelectedFeatures = null;

// 任意座標図郭の filter（属性の有無で外枠/詳細筆/低ズーム集約を判別）
// 詳細筆: 筆ID を持つ
const NINI_HUDE_FILTER = ["has", "筆ID"];
// 塗り（fill）用: 外枠＋低ズーム集約（都道府県 z4〜9 / 市区町村 z10〜11）＝詳細筆以外。
// 集約はデータ側でズーム帯排他のため、ズーム分岐は不要
const NINI_GAIWAKU_FILL_FILTER = ["!", ["has", "筆ID"]];
// 境界線（line）用: 外枠のみ（ファイル名を持つ）。
// 公共座標と同様、集約は塗りのみで境界線を付けない
const NINI_GAIWAKU_LINE_FILTER = [
  "all",
  ["has", "ファイル名"],
  ["!", ["has", "筆ID"]],
];
// 詳細筆かどうか（筆ID を持つフィーチャのみが操作対象）
const isNiniHude = (properties) => properties?.["筆ID"] != null;
// 非表示リスト（任意座標）の除外条件を合成（ファイル全体＋分割ブロック単位）
// 低ズーム集約はファイル名・分割番号を持たないため to-string で null → "" に落とし、式を安全に評価する（非表示リストには一致しない）
const withNiniNodisp = (f) => [
  "all",
  [
    "!",
    [
      "in",
      ["to-string", ["get", "ファイル名"]],
      ["literal", Arr_NoDispNiniFiles],
    ],
  ],
  [
    "!",
    [
      "in",
      [
        "concat",
        ["to-string", ["get", "ファイル名"]],
        ",",
        ["to-string", ["get", "分割番号"]],
      ],
      ["literal", Arr_NoDispNiniBlocks],
    ],
  ],
  f,
];

// 任意座標の詳細筆を表示する最小ズームレベル（未満は外枠のみ）
export const NINI_DETAIL_MIN_ZOOM = 15;

// 任意座標図郭の表示状態
let _isUserLoggedIn = false;
let _isNiniOn = true;
let _lastNiniZoomBucket = null; // "detail"(ZL15以上) / "gaiwaku"(ZL15未満)
const NINI_LAYERS = [
  "nini_kukaku_fill",
  "nini_kukaku_line",
  "nini_chibanlabel",
];

// 会員×ズームに応じて任意座標レイヤーの filter・可視性を適用する（引数省略時は現在の状態で再適用）
export const applyNiniFilter = (map, { isNiniOn, isUserLoggedIn } = {}) => {
  if (!map) return;
  if (typeof isNiniOn === "boolean") _isNiniOn = isNiniOn;
  if (typeof isUserLoggedIn === "boolean") _isUserLoggedIn = isUserLoggedIn;

  // 表示状態の切替時はマウスオーバー強調を解除
  if (map.getSource("nini_kukaku")) {
    map.removeFeatureState({ source: "nini_kukaku", sourceLayer: "hude" });
  }
  niniHoveredStateIdArray = [];

  // 表示OFF時は非表示にして終了
  if (!_isNiniOn) {
    NINI_LAYERS.forEach(
      (id) =>
        map.getLayer(id) && map.setLayoutProperty(id, "visibility", "none")
    );
    return;
  }
  NINI_LAYERS.forEach(
    (id) =>
      map.getLayer(id) && map.setLayoutProperty(id, "visibility", "visible")
  );
  // ゲスト＝常に外枠 / ログイン＝ZL15以上で詳細筆、未満で外枠（低ズーム集約含む）
  const showDetail = _isUserLoggedIn && map.getZoom() >= NINI_DETAIL_MIN_ZOOM;
  _lastNiniZoomBucket = showDetail ? "detail" : "gaiwaku";
  // 外枠表示時は塗りのみ集約を含め、境界線・ラベルは外枠のみ（公共座標の低ズーム帯の見た目に合わせる）
  const baseByLayer = {
    nini_kukaku_fill: showDetail ? NINI_HUDE_FILTER : NINI_GAIWAKU_FILL_FILTER,
    nini_kukaku_line: showDetail ? NINI_HUDE_FILTER : NINI_GAIWAKU_LINE_FILTER,
    nini_chibanlabel: showDetail ? NINI_HUDE_FILTER : NINI_GAIWAKU_LINE_FILTER,
  };
  NINI_LAYERS.forEach(
    (id) =>
      map.getLayer(id) && map.setFilter(id, withNiniNodisp(baseByLayer[id]))
  );
  // 冒頭の removeFeatureState で purchased も消えるため、購入履歴の反映を張り直す
  setOrderHistoryState(map);
};

export const maplibreInit = (
  mapContainerRef,
  isUserLoggedIn,
  addSelectedFeatures,
  deleteSelectedFeatures
) => {
  // 地物選択を画面に反映させるための関数を保持
  _addSelectedFeatures = addSelectedFeatures;
  _deleteSelectedFeatures = deleteSelectedFeatures;
  // 任意座標の表示制御用にログイン状態を保持
  _isUserLoggedIn = isUserLoggedIn;
  // 任意座標の初期表示状態（保存値があればそれを優先し、なければ表示ON）
  _isNiniOn = isUserLoggedIn
    ? loadState("checkMapState")?.isCheckNini ?? true
    : true;
  // pmtiles設定
  const protocol = new Protocol();
  maplibregl.addProtocol("pmtiles", protocol.tile);

  // 地図インスタンス生成
  const map = new maplibregl.Map({
    container: mapContainerRef.current,
    minZoom: 4,
    maxZoom: 20,
    maxBounds: [
      [120, 20],
      [155, 46.6666],
    ],
    style: getInitialStyle(isUserLoggedIn),
    center: [136.881074, 35.170474], //[135.194165, 34.693122]
    zoom: 16,
    hash: true,
    attributionControl: false,
    locale: {
      "GeolocateControl.FindMyLocation": "現在地表示",
      "GeolocateControl.LocationNotAvailable": "現在地表示は使用できません",
      "NavigationControl.ResetBearing": "回転傾きリセット",
      "NavigationControl.ZoomIn": "拡大",
      "NavigationControl.ZoomOut": "縮小",
    },
    preserveDrawingBuffer: true,
  });

  // コントロールの設定
  const measureControl = mapControlInit(map);
  // イベントの設定
  mapEventInit(map);

  return { map, measureControl };
};

// 再現設定に応じてスタイルを取得する
const getInitialStyle = (isUserLoggedIn) => {
  if (isUserLoggedIn) {
    const checkMapState = loadState("checkMapState");
    return checkMapState?.isCheckSaveState && checkMapState?.isCheckGray
      ? StyleURL_Gray
      : StyleURL;
  }
  return StyleURL;
};

const mapControlInit = (map, setDrawCtl) => {
  // ナビゲーション
  map.addControl(
    new maplibregl.NavigationControl({
      visualizePitch: true,
      showZoom: true,
      showCompass: true,
    }),
    "top-right"
  );

  // ジオロケーション
  map.addControl(
    new maplibregl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      fitBoundsOptions: {
        maxZoom: 18,
      },
      trackUserLocation: true,
    })
  );

  // アトリビューション
  const attributionControlArray = [mojcopyright];
  map.addControl(
    new maplibregl.AttributionControl({
      customAttribution: attributionControlArray,
      compact: false,
    }),
    "bottom-right"
  );

  // 計測機能 Terradraw measure
  const measureControl = measureCtlInit(map, setDrawCtl);

  return measureControl;
};

const mapEventInit = (map) => {
  map.on("load", () => {
    Map_Load_Layer_Func(map);
  });

  map.on("styledataloading", function () {
    map.once("styledata", function () {
      Map_Load_Layer_Func(map);
    });
  });

  let isMouseEnterHude;
  map.on("mouseenter", "hude", function () {
    isMouseEnterHude = true;
    // ZL12以上で選択用カーソルとする
    map.getCanvas().style.cursor = map.getZoom() >= 12 ? "pointer" : "";
  });

  map.on("mouseleave", "hude", function () {
    isMouseEnterHude = false;
    map.getCanvas().style.cursor = "";
  });

  map.on("zoomend", () => {
    // ZL変更後に選択可否状態を判定
    map.getCanvas().style.cursor =
      isMouseEnterHude && map.getZoom() >= 12 ? "pointer" : "";
    // 任意座標はZL15境界を跨いだら filter を再適用
    if (_isNiniOn && _isUserLoggedIn) {
      const bucket =
        map.getZoom() >= NINI_DETAIL_MIN_ZOOM ? "detail" : "gaiwaku";
      if (bucket !== _lastNiniZoomBucket) {
        applyNiniFilter(map);
      }
    }
  });

  map.on("mouseenter", "nini", function () {
    map.getCanvas().style.cursor = "pointer";
  });

  map.on("mouseleave", "nini", function () {
    map.getCanvas().style.cursor = "";
  });
};

// 計測機能の初期化
const measureCtlInit = (map) => {
  const measureControl = new MaplibreMeasureControl({
    modes: ["linestring", "select", "delete-selection", "delete"],
    open: true,
    distancePrecision: 1,
    modeOptions: {
      select: new (class extends TerraDrawSelectMode {
        // 計測機能のD&D中に、ノードが遅延してしまうため、見せないようにする
        onDragStart(event, setMapDraggability) {
          super.onDragStart(event, setMapDraggability);
          map.setLayoutProperty("td-measure-line-node", "visibility", "none");
        }
        onDragEnd(event, setMapDraggability) {
          super.onDragEnd(event, setMapDraggability);
          map.setLayoutProperty(
            "td-measure-line-node",
            "visibility",
            "visible"
          );
        }
      })(),
    },
    // 計測値ラベルの表示設定
    lineLayerLabelSpec: Object.assign(
      structuredClone(defaultMeasureControlOptions.lineLayerLabelSpec),
      {
        filter: ["==", "$type", "LineString"],
        layout: {
          "text-field": [
            "concat",
            ["to-string", ["get", "distance"]],
            " ",
            ["get", "unit"],
            [
              "case",
              ["==", ["get", "total"], 0],
              "",
              [
                "concat",
                "\n(",
                ["to-string", ["get", "total"]],
                " ",
                ["get", "totalUnit"],
                ")",
              ],
            ],
            [
              "case",
              ["all", ["has", "elevation"], [">", ["get", "elevation"], 0]],
              [
                "concat",
                "\nAlt. ",
                ["to-string", ["floor", ["get", "elevation"]]],
                " m",
              ],
              "",
            ],
          ],
          "symbol-placement": "line-center",
          "text-rotation-alignment": "map",
          "text-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            5,
            10,
            10,
            12.0,
            13,
            14.0,
            14,
            16.0,
            18,
            18.0,
          ],
          "text-overlap": "always",
          "text-justify": "center",
          "text-letter-spacing": 0.05,
        },
      }
    ),
  });
  // フォントの設定
  measureControl.fontGlyphs = ["Noto Sans JP Regular"];
  // 使用状態
  measureControl.on("mode-changed", (event) => {
    isDrawCtlUsing = event.mode !== "default";
  });

  const setPaintProperty = () => {
    measureControl.activate();
    // 破線に変更
    map.setPaintProperty("td-measure-linestring", "line-color", "#FBB03B");
    map.setPaintProperty("td-measure-linestring", "line-width", 2);
    map.setPaintProperty("td-measure-linestring", "line-dasharray", [2, 1.5]);
    // 終了点や編集点の色を合わせる
    map.setPaintProperty("td-measure-point", "circle-color", "#FBB03B");
    map.setPaintProperty("td-measure-point", "circle-radius", 3);
    map.setPaintProperty("td-measure-point", "circle-stroke-color", "#FFFFFF");
    map.setPaintProperty("td-measure-point", "circle-stroke-width", 2);
    map.setPaintProperty("td-measure-line-node", "circle-color", "#FBB03B");
    map.setPaintProperty(
      "td-measure-line-node",
      "circle-stroke-color",
      "#FFFFFF"
    );
  };
  measureControl.on("feature-deleted", (event) => {
    if (!event.deletedIds) {
      // 全削除した場合に再起動しておく（切替時の対策）
      measureControl.activate();
      setPaintProperty();
    }
  });

  map.addControl(measureControl);
  map.once("idle", () => {
    setPaintProperty();
  });

  // ツールチップ設定
  setMeasuerIconTooltip();

  return measureControl;
};

// 計測機能のツールチップを変更する
const setMeasuerIconTooltip = () => {
  const nameList = [
    ["maplibregl-terradraw-measure-add-linestring-button", "距離計測"],
    ["maplibregl-terradraw-measure-add-select-button", "選択"],
    ["maplibregl-terradraw-measure-delete-selection-button", "選択削除"],
    ["maplibregl-terradraw-measure-delete-button", "全削除"],
    ["maplibregl-terradraw-measure-download-button", "ダウンロード"],
  ];
  nameList.forEach((name) => {
    const btn = document.querySelector(`.${name[0]}`);
    if (btn) {
      btn.title = name[1];
    }
  });
};

// 切り替えボタン作成
export const createSwitchBtn = (isAddMeasuer) => {
  const btn = document.createElement("button");
  btn.id = "terradrawSwitchBtn";
  btn.type = "button";
  btn.classList.add("custom-control-btn");
  btn.title = isAddMeasuer ? "図形切替" : "計測切替";
  btn.style.backgroundImage = `url("${rightLeftIcon}")`;
  btn.style.backgroundPosition = "center";
  btn.style.backgroundRepeat = "no-repeat";
  btn.style.backgroundSize = "50%";
  document
    .querySelector(
      isAddMeasuer
        ? ".maplibregl-terradraw-measure-add-control"
        : ".maplibregl-terradraw-add-control"
    )
    .parentNode.appendChild(btn);
  return btn;
};
// 切り替えボタンイベント設定
export const setSwitchBtnClickHandler = (options) => {
  const { btn, control, isAddMeasuer, setDrawCtl } = options;

  if (isAddMeasuer) {
    btn.addEventListener("click", () => {
      // 選択解除
      deselectAllFeature(control);
      setTimeout(() => {
        // 選択解除までの時間を空けて実行
        const terradrawControl = switchTerradrawCtl(options);
        setDrawCtl(terradrawControl);
      }, 100);
    });
  } else {
    btn.addEventListener("click", () => {
      // 選択解除
      deselectAllFeature(control);
      setTimeout(() => {
        // 選択解除までの時間を空けて実行
        const measureControl = switchMeasureCtl(options);
        setDrawCtl(measureControl);
      }, 100);
    });
  }
};

// 全Featureの選択を解除する
const deselectAllFeature = (control) => {
  const terradrawInstance = control.getTerraDrawInstance();
  terradrawInstance.getSnapshot().forEach((feature) => {
    feature?.properties?.selected &&
      terradrawInstance.deselectFeature(feature.id);
  });
};

// 図形機能切替
export const switchTerradrawCtl = (options) => {
  const { map, drawColor, control } = options;
  const measureControl = control;
  // 選択解除
  deselectAllFeature(measureControl);
  // 計測機能で作成したfeatureを保存する
  const measureSourceAndLayers = getTerradrawSourceAndLayers(map, true);
  // コピーの図形レイヤを取得する
  const drawSourceAndLayers = getTerradrawSourceAndLayers(map, false);
  // 計測機能削除
  map.removeControl(measureControl);

  // 図形コントロール作成
  const terradrawControl = terradrawCtlInit(options);
  // 作成完了時に、個別の色を指定する
  terradrawControl
    .getTerraDrawInstance()
    .on("finish", (finishedId, context) => {
      if (context.action === "draw" && !drawColor[finishedId]) {
        if (context.mode === "linestring") {
          drawColor[finishedId] = drawColor.default.lineString;
        } else if (context.mode === "polygon") {
          drawColor[finishedId] = drawColor.default.polygon;
        }
      }
    });

  // 保存レイヤ登録
  addTerradrawSourceAndLayers(map, measureSourceAndLayers);
  // コピーを図形コントロールに復元
  recoverSourceAndLayers(map, terradrawControl, drawSourceAndLayers);

  // 切り替えボタンの作成
  const btn = createSwitchBtn(!IS_ADD_MEASURE);
  const newOptions = {
    ...options,
    btn,
    control: terradrawControl,
    isAddMeasuer: !IS_ADD_MEASURE,
  };
  setSwitchBtnClickHandler(newOptions);
  return terradrawControl;
};

// 図形機能の初期化
const terradrawCtlInit = (options) => {
  const { map } = options;
  const terradrawControl = new MaplibreTerradrawControl({
    modes: ["linestring", "polygon", "select", "delete-selection", "delete"],
    adapterOptions: { prefixId: "td" },
    modeOptions: {
      linestring: new TerraDrawLineStringMode(),
      polygon: new TerraDrawPolygonMode(),
    },
  });
  // 使用状態
  terradrawControl.on("mode-changed", (event) => {
    isDrawCtlUsing = event.mode !== "default";
  });
  terradrawControl.on("feature-deleted", (event) => {
    if (!event.deletedIds) {
      // 全削除した場合に再起動しておく（切替時の対策）
      terradrawControl.activate();
    }
  });
  map.addControl(terradrawControl, "top-right");
  // ツールチップ設定
  setTerradrawIconTooltip();
  // 色設定ボタン作成
  createPaletteBtn();
  // ダウンロードボタン作成
  createDownloadBtn(map);
  // アップロードボタン作成
  createUploadBtn(terradrawControl.getTerraDrawInstance(), options);

  return terradrawControl;
};

// 図形機能のツールチップを変更する
const setTerradrawIconTooltip = () => {
  const nameList = [
    ["maplibregl-terradraw-render-button", "開閉"],
    ["maplibregl-terradraw-add-linestring-button", "線入力"],
    ["maplibregl-terradraw-add-polygon-button", "面入力"],
    ["maplibregl-terradraw-add-select-button", "選択"],
    ["maplibregl-terradraw-delete-selection-button", "選択削除"],
    ["maplibregl-terradraw-delete-button", "全削除"],
  ];
  nameList.forEach((name) => {
    const btn = document.querySelector(`.${name[0]}`);
    if (btn) {
      btn.title = name[1];
    }
  });
};

// 色設定ボタン作成
const createPaletteBtn = () => {
  const btn = document.createElement("button");
  btn.id = "terradrawColorSetting";
  btn.type = "button";
  btn.classList.add("custom-control-btn");
  btn.disabled = true;
  btn.title = "色設定";
  btn.style.backgroundImage = `url("${paletteIcon}")`;
  btn.style.backgroundPosition = "center";
  btn.style.backgroundRepeat = "no-repeat";
  btn.style.backgroundSize = "80%";
  document
    .querySelector(".maplibregl-terradraw-add-control")
    ?.parentNode?.appendChild(btn);
  btn.onclick = () => {
    document.querySelector("#individualDrawColorSettingBtn")?.click();
  };
  return btn;
};

// ダウンロードボタン作成
const createDownloadBtn = (map) => {
  const btn = document.createElement("button");
  btn.id = "terradrawFeatureDownload";
  btn.type = "button";
  btn.classList.add("custom-control-btn");
  btn.title = "ダウンロード";
  btn.style.backgroundImage = `url("${downloadIcon}")`;
  btn.style.backgroundPosition = "center";
  btn.style.backgroundRepeat = "no-repeat";
  btn.style.backgroundSize = "80%";
  document
    .querySelector(".maplibregl-terradraw-add-control")
    ?.parentNode?.appendChild(btn);
  btn.onclick = () => {
    // 出力データ
    const geoJson = { type: "FeatureCollection", features: [] };
    Object.keys(map.getStyle().sources)
      .filter((key) => key.startsWith("td-") && !key.includes("measure"))
      .forEach((key) => {
        map
          .getSource(key)
          ._data.features.filter(
            (feature) => feature?.properties?.mode !== "select"
          )
          .forEach((feature) => {
            geoJson.features.push({
              type: feature.type,
              geometry: feature.geometry,
              properties: feature.properties,
            });
          });
      });
    // ファイルダウンロード
    const blob = new Blob([JSON.stringify(geoJson, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "feature.geojson";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  return btn;
};

// アップロードボタン作成
const createUploadBtn = (drawInstance, options) => {
  const { drawColor, setToast } = options;
  const btn = document.createElement("button");
  btn.id = "terradrawFeatureUpload";
  btn.type = "button";
  btn.classList.add("custom-control-btn");
  btn.title = "アップロード";
  btn.style.backgroundImage = `url("${uploadIcon}")`;
  btn.style.backgroundPosition = "center";
  btn.style.backgroundRepeat = "no-repeat";
  btn.style.backgroundSize = "80%";
  document
    .querySelector(".maplibregl-terradraw-add-control")
    ?.parentNode?.appendChild(btn);

  // ファイル入力
  const fileInput = document.createElement("input");
  fileInput.type = "file";
  fileInput.accept = ".geojson";
  fileInput.style.display = "none";
  fileInput.onchange = (e) => {
    const files = e.target.files;
    if (!(files && files[0])) {
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const geoJson = JSON.parse(e.target.result);
        !drawInstance.enabled && drawInstance.start();

        const addedCount = addFeaturesToTerradrawWithColor(
          drawInstance,
          geoJson?.features,
          drawColor
        );

        setToast({
          title: "読込完了",
          message: `${addedCount}件のデータを読み込みました`,
          type: "light",
        });
      } catch (error) {
        console.error(error);
        setToast({
          title: "エラー",
          message: `ファイルの読み込みに失敗しました：\n${error}`,
          type: "danger",
        });
        return;
      }
    };
    reader.readAsText(files[0]);
    e.target.value = "";
  };

  btn.appendChild(fileInput);
  btn.onclick = () => {
    fileInput.click();
  };

  return btn;
};

// 色情報を持つFeatureをTerradrawに追加する
const addFeaturesToTerradrawWithColor = (drawInstance, features, drawColor) => {
  // idを取得して設定
  features.forEach((feature) => (feature.id = drawInstance.getFeatureId()));

  // 色情報を設定
  features.forEach((feature) => {
    // 色情報取得
    const { mode, selected, ...color } = feature?.properties;
    switch (mode) {
      case "linestring":
        if (
          checkColor(color.lineStringColor) &&
          checkWidth(color.lineStringWidth)
        ) {
          drawColor[feature.id] = color;
        }
        break;
      case "polygon":
        if (
          checkColor(color.polygonFillColor) &&
          checkOpacity(color.polygonFillOpacity) &&
          checkColor(color.polygonOutlineColor) &&
          checkWidth(color.polygonOutlineWidth)
        ) {
          drawColor[feature.id] = {
            fillColor: color.polygonFillColor,
            fillOpacity: color.polygonFillOpacity,
            outlineColor: color.polygonOutlineColor,
            outlineWidth: color.polygonOutlineWidth,
          };
        }
        break;
      default:
    }
  });

  // Terradrawに追加
  const ret = drawInstance.addFeatures(features);
  //成功した図形のカウント
  const count = ret.filter(item => item.valid === true).length;
  return count;
};

// 計測機能切替
const switchMeasureCtl = (options) => {
  const { map, control } = options;
  const terradrawControl = control;
  // 選択解除
  deselectAllFeature(terradrawControl);
  // 図形機能で作成したfeatureを保存する
  const drawSourceAndLayers = getTerradrawSourceAndLayers(map, false);
  // コピーの計測レイヤを取得する
  const measureSourceAndLayers = getTerradrawSourceAndLayers(map, true);
  // 図形コントロール削除
  map.removeControl(terradrawControl);

  // 計測コントロールの作成
  const measureControl = measureCtlInit(map);

  // 保存レイヤ登録
  addTerradrawSourceAndLayers(map, drawSourceAndLayers);
  // コピーを計測コントロールに復元
  recoverSourceAndLayers(map, measureControl, measureSourceAndLayers);

  // 切り替えボタンの作成
  const btn = createSwitchBtn(IS_ADD_MEASURE);
  const newOptions = {
    ...options,
    btn,
    control: measureControl,
    isAddMeasuer: IS_ADD_MEASURE,
  };
  setSwitchBtnClickHandler(newOptions);
  return measureControl;
};

// Terradrawで作成したFeatureのソースとレイヤを取得する
const getTerradrawSourceAndLayers = (map, isMeasure) => {
  const checkIdFunc = isMeasure
    ? (id) => {
      return id.startsWith("td-measure");
    }
    : (id) => {
      return id.startsWith("td-") && !id.includes("td-measure");
    };
  const style = map.getStyle();
  const terradrawSource = Object.keys(style.sources).reduce((acc, key) => {
    if (checkIdFunc(key)) {
      const orgSource = map.getSource(key);
      acc[key] = { type: orgSource.type, data: orgSource._data };
    }
    return acc;
  }, {});
  const terradrawLayers = style.layers.reduce((acc, layer) => {
    if (checkIdFunc(layer.id)) {
      acc.push(layer);
    }
    return acc;
  }, []);
  return { source: terradrawSource, layers: terradrawLayers };
};
// 保持していたソースとレイヤを追加する
const addTerradrawSourceAndLayers = (map, sourceAndLayers) => {
  Object.entries(sourceAndLayers.source).forEach(([key, value]) => {
    map.addSource(key, value);
  });
  sourceAndLayers.layers.forEach((layer) => {
    map.addLayer(layer);
  });
};
// 保持していたソースとレイヤをコントロールへ復元する
const recoverSourceAndLayers = (map, control, sourceAndLayers) => {
  // 地図からレイヤーを削除
  sourceAndLayers.layers.forEach((layer) => {
    map.removeLayer(layer.id);
  });
  // 地図からソースを削除
  Object.keys(sourceAndLayers.source).forEach((key) => {
    map.removeSource(key);
  });
  control.activate();
  const drawInstance = control.getTerraDrawInstance();
  !drawInstance.enabled && drawInstance.start();
  Object.values(sourceAndLayers.source).forEach((value) => {
    value?.data?.features && drawInstance.addFeatures(value?.data?.features);
  });
};

const Map_Load_Layer_Func = (map) => {
  // ソース設定
  mapAddSources(map);
  // レイヤ設定
  mapAddLayers(map);
  // フィルタ設定
  mapSetFilters(map);
  // 任意座標の filter・可視性を再適用
  applyNiniFilter(map);

  //3D都市モデルの表示非表示設定
  Func_chk_PLATEAU_Building_Visibility(map);
  Func_chk_plateau_dem(map);
  //初期表示時の傾きによってレイヤー制御
  let lastPitch = -1; //map.getPitch(); // 初期のピッチ値を保存
  Event_Change_Pitch();

  //地図の傾き（ピッチ）が変わった際のイベント
  function Event_Change_Pitch() {
    const currentPitch = map.getPitch();

    if (currentPitch !== lastPitch) {
      if (currentPitch > 30) {
        map.moveLayer("plateau_bldg", "mapple_傾き部品注記");
      } else {
        map.moveLayer("plateau_bldg", "mapple_冬期閉鎖");
      }
      lastPitch = currentPitch;
    }
  }

  // ポイントクリックイベント
  function Function_Building_Click(e) {
    // 計測・図形機能使用中
    if (isDrawCtlUsing) {
      return;
    }
    // 建物クリック時に、地物の情報を取得して表示する
    //クリック時にプロパティを持っていない地物の場合（ZL12未満の都道府県・市区町村ポリゴン）は、マーカー移動や属性表示（画面更新）を行わない）
    if (e.features[0]["properties"]) {
      //マーカーの移動
      marker.setLngLat([e.lngLat.lng, e.lngLat.lat]).addTo(map);
      // 右スライダーに情報表示
      _addSelectedFeatures(e.features[0]);
      // 右スライダーを開く
      window.dispatchEvent(new CustomEvent("openRightSide"));
    }
  }

  map.on("click", function (e) {
    const features = map.queryRenderedFeatures(e.point, {
      layers: ["plateau_bldg", "hude", "plateau_bldg_fill", "nini_kukaku_fill"], // 対象レイヤーを指定
    });

    if (features.length > 0) {
      // 右スライダ―の情報削除
      _deleteSelectedFeatures();
    }
  });

  map.on("click", "plateau_bldg", function (e) {
    Function_Building_Click(e);
  });
  // カーソルON,OFF
  map.on("mouseenter", "plateau_bldg", function () {
    map.getCanvas().style.cursor = "pointer";
  });
  map.on("mouseleave", "plateau_bldg", function () {
    map.getCanvas().style.cursor = "";
  });
  map.on("click", "plateau_bldg_fill", function (e) {
    Function_Building_Click(e);
  });
  // カーソルON,OFF
  map.on("mouseenter", "plateau_bldg_fill", function () {
    map.getCanvas().style.cursor = "pointer";
  });
  map.on("mouseleave", "plateau_bldg_fill", function () {
    map.getCanvas().style.cursor = "";
  });

  GsiPhotographMinZoom(map);

  map.on("moveend", function () {
    GsiPhotographMinZoom(map);
    setOrderHistoryState(map); // 購入履歴反映
  });

  // 任意座標の表示ON直後はタイルのロード完了を待って購入履歴の反映を行う
  // （非表示中はタイルが取得されず、moveend も発火しないため）
  map.on("sourcedata", function (e) {
    if (e.sourceId === "nini_kukaku" && e.isSourceLoaded) {
      setOrderHistoryState(map); // 購入履歴反映
    }
  });
  map.on("move", function () {
    Event_Change_Pitch();
  });

  map.on("mousemove", "hude", function (e) {
    if (isDrawCtlUsing) {
      // 計測機能利用中は色を変えない
      return;
    }
    if (e.features.length > 0) {
      if (hoveredStateIdArray) {
        hoveredStateIdArray.forEach((hoveredStateId) => {
          map.setFeatureState(
            { source: "moj", sourceLayer: "hude", id: hoveredStateId },
            { hover: false }
          );
        });
      }

      hoveredStateIdArray = [];

      e.features.forEach((feat) => {
        hoveredStateIdArray.push(feat.id);
      });

      hoveredStateIdArray.forEach((hoveredStateId) => {
        map.setFeatureState(
          { source: "moj", sourceLayer: "hude", id: hoveredStateId },
          { hover: true }
        );
      });
    }
  });

  map.on("mouseleave", "hude", function () {
    if (hoveredStateIdArray) {
      hoveredStateIdArray.forEach((hoveredStateId) => {
        map.setFeatureState(
          { source: "moj", sourceLayer: "hude", id: hoveredStateId },
          { hover: false }
        );
      });
    }
    hoveredStateIdArray = [];
  });

  let marker = new maplibregl.Marker({ offset: [0, -18] });
  // クリック時に、地物の情報を取得して表示する
  map.on("click", "hude", function (e) {
    // 計測・図形機能使用中
    if (isDrawCtlUsing) {
      return;
    }
    //クリック時にファイル名を持っていない地物の場合（ZL12未満の都道府県・市区町村ポリゴン）は、マーカー移動や属性表示（画面更新）を行わない）
    if (e.features[0]["properties"]["ファイル名"]) {
      //マーカーの移動
      marker.setLngLat([e.lngLat.lng, e.lngLat.lat]).addTo(map);
      // 右スライダーに情報表示（クリック点は範囲ダウンロードの中心に使う）
      _addSelectedFeatures(e.features, { lngLat: e.lngLat });
      // 右スライダーを開く
      window.dispatchEvent(new CustomEvent("openRightSide"));
    }
  });

  map.on("click", "nini", function (e) {
    // 計測・図形機能使用中
    if (isDrawCtlUsing) {
      return;
    }
    var FileName = JSON.parse(e.features[0].properties.FileName);
    window.dispatchEvent(
      new CustomEvent("showNiniMapModal", { detail: { file: FileName } })
    );
  });

  // 任意座標の詳細筆クリック時に、地物の情報を取得して表示する
  map.on("click", "nini_kukaku_fill", function (e) {
    // 計測・図形機能使用中
    if (isDrawCtlUsing) {
      return;
    }
    //クリック時に詳細筆でない地物の場合（外枠）は、マーカー移動や属性表示（画面更新）を行わない
    if (isNiniHude(e.features[0]["properties"])) {
      //マーカーの移動
      marker.setLngLat([e.lngLat.lng, e.lngLat.lat]).addTo(map);
      // 右スライダーに情報表示
      _addSelectedFeatures(e.features, { kind: "nini", lngLat: e.lngLat });
      // 右スライダーを開く
      window.dispatchEvent(new CustomEvent("openRightSide"));
    }
  });

  // カーソルON,OFF
  map.on("mouseenter", "nini_kukaku_fill", function (e) {
    // 詳細筆のみ選択用カーソルとする
    if (isNiniHude(e.features[0]["properties"])) {
      map.getCanvas().style.cursor = "pointer";
    }
  });
  map.on("mouseleave", "nini_kukaku_fill", function () {
    map.getCanvas().style.cursor = "";
  });

  map.on("mousemove", "nini_kukaku_fill", function (e) {
    if (isDrawCtlUsing) {
      // 計測機能利用中は色を変えない
      return;
    }
    if (e.features.length > 0) {
      if (niniHoveredStateIdArray) {
        niniHoveredStateIdArray.forEach((hoveredStateId) => {
          map.setFeatureState(
            { source: "nini_kukaku", sourceLayer: "hude", id: hoveredStateId },
            { hover: false }
          );
        });
      }

      niniHoveredStateIdArray = [];

      e.features.forEach((feat) => {
        // 外枠・低ズーム集約は強調対象外（これらにも feature.id が付くため筆IDで判別する）
        if (feat.id !== undefined && isNiniHude(feat.properties)) {
          niniHoveredStateIdArray.push(feat.id);
        }
      });

      niniHoveredStateIdArray.forEach((hoveredStateId) => {
        map.setFeatureState(
          { source: "nini_kukaku", sourceLayer: "hude", id: hoveredStateId },
          { hover: true }
        );
      });
    }
  });

  map.on("mouseleave", "nini_kukaku_fill", function () {
    if (niniHoveredStateIdArray) {
      niniHoveredStateIdArray.forEach((hoveredStateId) => {
        map.setFeatureState(
          { source: "nini_kukaku", sourceLayer: "hude", id: hoveredStateId },
          { hover: false }
        );
      });
    }
    niniHoveredStateIdArray = [];
  });

  setOrderHistoryState(map); // 購入履歴反映
};

const mapAddSources = (map) => {
  map.addSource("moj", {
    type: "vector",
    tiles: [process.env.REACT_APP_MOJMVT_URL],
    minzoom: 4,
    maxzoom: 16,
  });

  map.addSource("gsiphoto", {
    type: "raster",
    tiles: [
      "https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg",
    ],
    tileSize: 256,
    attribution:
      '<a href="https://maps.gsi.go.jp/development/ichiran.html" target="_blank" rel="noopener noreferrer">国土地理院</a>',
    minzoom: 16,
    maxzoom: 18,
  });

  //3D都市モデル地形
  map.addSource("plateau_dem", {
    type: "raster-dem",
    encoding: "terrarium",
    url: `pmtiles://${process.env.REACT_APP_PLATEAU_DEM_URL}`,
    minzoom: 5,
    maxzoom: 16,
    attribution:
      '<a href="https://www.mlit.go.jp/plateau/" target="_blank" rel="noopener noreferrer">PLATEAU</a>',
  });

  map.addSource("plateau", {
    type: "vector",
    url: `pmtiles://${process.env.REACT_APP_PLATEAU_URL}`,
    minzoom: 15,
    attribution:
      '<a href="https://www.mlit.go.jp/plateau/" target="_blank" rel="noopener noreferrer">PLATEAU</a>',
  });

  // 任意座標図郭（外枠・詳細筆を統合したMVT）
  // ズーム範囲は配信データに合わせて明示する（ZL17以上はZL16のタイルをオーバーズームして描画）
  map.addSource("nini_kukaku", {
    type: "vector",
    tiles: [process.env.REACT_APP_NINI_MVT_URL],
    minzoom: 4,
    maxzoom: 16,
    attribution: mojcopyright,
  });
};

const mapAddLayers = (map) => {
  let shadowcolor;
  if (document.getElementById("chk_gray").checked) {
    shadowcolor = "#979797";
  } else {
    shadowcolor = "#5fbf5f";
  }
  map.addLayer(
    {
      id: "plateau_hills",
      type: "hillshade",
      source: "plateau_dem",
      layout: {
        visibility: "visible",
      },
      paint: {
        "hillshade-shadow-color": shadowcolor,
      },
    },
    "mapple_L3_coastline"
  );

  map.addLayer(
    {
      id: "plateau_bldg_fill_outline",
      type: "line",
      source: "plateau",
      "source-layer": "bldg",
      maxzoom: 19,
      paint: {
        "line-width": 0.5,
        "line-color": "#A0A0A0",
        "line-opacity": [
          "interpolate",
          ["linear"],
          ["zoom"],
          15,
          0,
          16,
          1,
          18,
          1,
          19,
          0,
        ],
      },
    },
    "mapple_冬期閉鎖"
  );

  map.addLayer(
    {
      id: "plateau_bldg_fill",
      type: "fill",
      source: "plateau",
      "source-layer": "bldg",
      maxzoom: 19,
      paint: {
        "fill-color": [
          "match",
          ["get", "usage"],
          ["421", "422", "453"],
          "#FFA6BC",
          ["402", "404"],
          "#FFB200",
          "403",
          "#7BBADE",
          "#E0E0E0",
        ],
        "fill-opacity": [
          "interpolate",
          ["linear"],
          ["zoom"],
          15,
          0,
          16,
          1,
          18,
          1,
          19,
          0,
        ],
      },
    },
    "plateau_bldg_fill_outline"
  );

  map.addLayer(
    {
      id: "plateau_bldg",
      type: "fill-extrusion",
      source: "plateau",
      "source-layer": "bldg",
      maxzoom: 19,
      paint: {
        "fill-extrusion-height": ["coalesce", ["get", "height"], 0],
        "fill-extrusion-base": 0,
        "fill-extrusion-color": [
          "interpolate-lab",
          ["linear"],
          ["%", ["round", ["*", 10, ["coalesce", ["get", "height"], 0]]], 10],
          0,
          [
            "match",
            ["get", "usage"],
            ["421", "422", "453"],
            "#FFA6BC",
            ["402", "404"],
            "#FFB200",
            "403",
            "#7BBADE",
            "#A0A0A0",
          ],
          1,
          [
            "match",
            ["get", "usage"],
            ["421", "422", "453"],
            "#FFAAC0",
            ["402", "404"],
            "#FFB604",
            "403",
            "#7FBEE2",
            "#A4A4A4",
          ],
          2,
          [
            "match",
            ["get", "usage"],
            ["421", "422", "453"],
            "#FFAEC4",
            ["402", "404"],
            "#FFBA08",
            "403",
            "#83C2E6",
            "#A8A8A8",
          ],
          3,
          [
            "match",
            ["get", "usage"],
            ["421", "422", "453"],
            "#FFB2C8",
            ["402", "404"],
            "#FFBE0C",
            "403",
            "#87C6EA",
            "#ACACAC",
          ],
          4,
          [
            "match",
            ["get", "usage"],
            ["421", "422", "453"],
            "#FFB6CC",
            ["402", "404"],
            "#FFC210",
            "403",
            "#8BCAEE",
            "#B0B0B0",
          ],
          5,
          [
            "match",
            ["get", "usage"],
            ["421", "422", "453"],
            "#FFBAD0",
            ["402", "404"],
            "#FFC614",
            "403",
            "#8BCEF2",
            "#B4B4B4",
          ],
          6,
          [
            "match",
            ["get", "usage"],
            ["421", "422", "453"],
            "#FFBED4",
            ["402", "404"],
            "#FFCA18",
            "403",
            "#8BD2F6",
            "#B8B8B8",
          ],
          7,
          [
            "match",
            ["get", "usage"],
            ["421", "422", "453"],
            "#FFC2D8",
            ["402", "404"],
            "#FFCE1C",
            "403",
            "#8FD6FA",
            "#BCBCBC",
          ],
          8,
          [
            "match",
            ["get", "usage"],
            ["421", "422", "453"],
            "#FFC6DC",
            ["402", "404"],
            "#FFD220",
            "403",
            "#93DAFE",
            "#C0C0C0",
          ],
          9,
          [
            "match",
            ["get", "usage"],
            ["421", "422", "453"],
            "#FFCAE0",
            ["402", "404"],
            "#FFD624",
            "403",
            "#97DEFF",
            "#C4C4C4",
          ],
        ],

        "fill-extrusion-opacity": [
          "interpolate",
          ["linear"],
          ["zoom"],
          15,
          0,
          16,
          1,
          18,
          1,
          19,
          0,
        ],
      },
    },
    "mapple_傾き部品注記"
  );

  map.addLayer(
    {
      id: "gsiphoto",
      type: "raster",
      source: "gsiphoto",
      minzoom: 17,
      paint: {
        "raster-opacity": ["interpolate", ["linear"], ["zoom"], 17, 0, 18, 1],
      },
    },
    "mapple_冬期閉鎖"
  );

  map.addLayer(
    {
      id: "hude",
      type: "fill",
      source: "moj",
      "source-layer": "hude",
      filter: ["!", ["in", ["get", "ファイル名"], ["literal", Arr_NoDispMap]]],
      paint: {
        "fill-color": [
          "case",
          ["boolean", ["feature-state", "purchased"], false],
          HUDE_COLORS.purchased_fill, // 購入済みの色: 青
          "#ff0000", // デフォルトの色: 赤
        ],
        "fill-antialias": false,
        "fill-opacity": [
          "step",
          ["zoom"],
          F_Hude_fill_opacity,
          12,
          [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            0.5,
            F_Hude_fill_opacity,
          ],
        ],
      },
    },
    "mapple_部品注記"
  );

  map.addLayer(
    {
      id: "hude-borders",
      type: "line",
      source: "moj",
      "source-layer": "hude",
      minzoom: 12,
      filter: ["!", ["in", ["get", "ファイル名"], ["literal", Arr_NoDispMap]]],
      paint: {
        "line-color": "#ff0000",
        "line-width": 0.5,
      },
    },
    "mapple_部品注記"
  );

  // 任意座標図郭（filter・可視性は applyNiniFilter が制御）
  map.addLayer(
    {
      id: "nini_kukaku_fill",
      type: "fill",
      source: "nini_kukaku",
      "source-layer": "hude",
      layout: { visibility: "none" },
      filter: withNiniNodisp(NINI_GAIWAKU_FILL_FILTER),
      paint: {
        "fill-color": [
          "case",
          ["boolean", ["feature-state", "purchased"], false],
          HUDE_COLORS.purchased_fill, // 購入済みの色: 公共と同じ青
          HUDE_COLORS.nini_fill, // デフォルトの色: 赤系
        ],
        "fill-antialias": false,
        "fill-opacity": [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          0.5,
          F_Hude_fill_opacity,
        ],
      },
    },
    "mapple_部品注記"
  );

  map.addLayer(
    {
      id: "nini_kukaku_line",
      type: "line",
      source: "nini_kukaku",
      "source-layer": "hude",
      layout: { visibility: "none" },
      filter: withNiniNodisp(NINI_GAIWAKU_LINE_FILTER),
      paint: {
        "line-color": HUDE_COLORS.nini_line,
        "line-width": 0.5,
      },
    },
    "mapple_部品注記"
  );

  map.addLayer({
    id: "nini_heatmap",
    type: "heatmap",
    source: "moj",
    "source-layer": "nini",
    maxzoom: 12,
    paint: {
      "heatmap-weight": 1,
      "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 4, 20, 12, 50],
      "heatmap-color": [
        "interpolate",
        ["linear"],
        ["heatmap-density"],
        0,
        "rgba(255,255,0,0)",
        0.1,
        "rgba(255,255,0,0.1)",
        1,
        "rgb(255,255,0)",
      ],
      "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 4, 2, 12, 60],
      "heatmap-opacity": ["interpolate", ["linear"], ["zoom"], 4, 0.1, 12, 0.2],
    },
  });

  map.addLayer({
    id: "chibanpoint",
    type: "heatmap",
    source: "moj",
    "source-layer": "point",
    maxzoom: 12,
    paint: {
      "heatmap-weight": 1,
      "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 4, 20, 12, 50],
      "heatmap-color": [
        "interpolate",
        ["linear"],
        ["heatmap-density"],
        0,
        "rgba(255,0,0,0)",
        0.1,
        "rgba(255,0,0,0.1)",
        1,
        "rgb(255,0,0)",
      ],
      "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 4, 2, 12, 60],
      "heatmap-opacity": ["interpolate", ["linear"], ["zoom"], 4, 0.1, 12, 0.2],
    },
  });

  map.addLayer({
    id: "koukyou",
    type: "symbol",
    source: "mapple",
    "source-layer": "P0T0",
    filter: [
      "in",
      ["get", "code"],
      [
        "literal",
        [
          "T0aabaa@@",
          "T0aabab@@",
          "T0aabac@@",
          "T0aabad@@",
          "T0aabae@@",
          "T0aabaf@@",
          "T0aabbc@@",
          "T0aabbd@@",
          "T0aabbe@@",
          "T0aabbf@@",
          "T0aabkc@@",
          "T0aabkd@@",
          "T0aabke@@",
          "T0aabkf@@",
          "T0aablc@@",
          "T0aabld@@",
          "T0aable@@",
          "T0aablf@@",
          "T0aabmc@@",
          "T0aabmd@@",
          "T0aabmf@@",
        ],
      ],
    ],
    layout: {
      "text-offset": ["literal", [0, bizyoffset]],
      "text-field": ["get", "text"],
      "text-size": [
        "let",
        "size",
        9.868,
        [
          "interpolate",
          ["linear"],
          ["zoom"],
          10,
          ["var", "size"],
          16,
          ["*", 2, ["var", "size"]],
          20,
          ["*", 8, ["var", "size"]],
        ],
      ],
      "text-font": ["literal", [BizFontName]],
    },
    paint: {
      "text-color": "#FFFFFF",
      "text-halo-color": "#000000",
      "text-halo-width": 1.2,
    },
  });

  map.addLayer({
    id: "koukyou_LINE",
    type: "symbol",
    source: "mapple",
    "source-layer": "P0T0_LINE",
    filter: [
      "in",
      ["get", "code"],
      [
        "literal",
        [
          "T0aabaa@@",
          "T0aabab@@",
          "T0aabac@@",
          "T0aabad@@",
          "T0aabae@@",
          "T0aabaf@@",
          "T0aabbc@@",
          "T0aabbd@@",
          "T0aabbe@@",
          "T0aabbf@@",
          "T0aabkc@@",
          "T0aabkd@@",
          "T0aabke@@",
          "T0aabkf@@",
          "T0aablc@@",
          "T0aabld@@",
          "T0aable@@",
          "T0aablf@@",
          "T0aabmc@@",
          "T0aabmd@@",
          "T0aabmf@@",
        ],
      ],
    ],
    layout: {
      "symbol-placement": "line-center",
      "text-pitch-alignment": "viewport",
      "text-justify": "auto",
      "text-radial-offset": 0.5,
      "text-writing-mode": ["horizontal", "vertical"],
      "text-rotation-alignment": "map",
      "text-allow-overlap": false,
      "icon-allow-overlap": false,
      "text-offset": ["literal", [0, bizyoffset]],
      "text-field": ["get", "text"],
      "text-size": [
        "let",
        "size",
        9.868,
        [
          "interpolate",
          ["linear"],
          ["zoom"],
          10,
          ["var", "size"],
          16,
          ["*", 2, ["var", "size"]],
          20,
          ["*", 8, ["var", "size"]],
        ],
      ],
      "text-font": ["literal", [BizFontName]],
    },
    paint: {
      "text-color": "#FFFFFF",
      "text-halo-color": "#000000",
      "text-halo-width": 1.2,
    },
  });

  map.addLayer({
    id: "nini",
    type: "symbol",
    source: "moj",
    "source-layer": "nini",
    minzoom: 12,
    layout: {
      "icon-offset": ["literal", [0, 22]],
      "icon-size": ["interpolate", ["linear"], ["zoom"], 10, 0.5, 16, 1, 20, 4],
      "text-offset": ["literal", [0, bizyoffset]],
      "icon-image": "cyoumei_whiteyellow_tate",
      "text-anchor": "center",
      "text-max-width": 256,
      "text-field": ["get", "SIF_OAZA"],
      "text-size": [
        "let",
        "size",
        9.868,
        [
          "interpolate",
          ["linear"],
          ["zoom"],
          10,
          ["var", "size"],
          16,
          ["*", 2, ["var", "size"]],
          20,
          ["*", 8, ["var", "size"]],
        ],
      ],
      "text-font": ["literal", [BizFontNameNINI]],
    },
    paint: {
      "text-color": "#393C00",
      "text-halo-color": "#FFFFFF",
      "text-halo-width": 1.2,
    },
  });

  // 公共座標の地番ラベル（ベクトルソースの筆ポリゴンに直接シンボル配置する）
  map.addLayer({
    id: "chibanlabel",
    type: "symbol",
    source: "moj",
    "source-layer": "hude",
    minzoom: 18,
    filter: ["!", ["in", ["get", "ファイル名"], ["literal", Arr_NoDispMap]]],
    layout: {
      "text-field": ["get", "地番"],
      "text-allow-overlap": true,
      "text-size": 14,
      "text-font": ["literal", [BizFontName]],
      "text-offset": ["literal", [0, bizyoffset]],
    },
    paint: {
      "text-halo-color": "#BBBBBB",
      "text-halo-width": 0.8,
    },
  });

  // 任意座標の地番ラベル（公共座標の chibanlabel と同スタイル。filter・可視性は applyNiniFilter が制御）
  map.addLayer({
    id: "nini_chibanlabel",
    type: "symbol",
    source: "nini_kukaku",
    "source-layer": "hude",
    minzoom: 18,
    layout: {
      visibility: "none",
      "text-field": ["get", "地番"],
      "text-allow-overlap": true,
      "text-size": 14,
      "text-font": ["literal", [BizFontName]],
      "text-offset": ["literal", [0, bizyoffset]],
    },
    filter: withNiniNodisp(NINI_GAIWAKU_LINE_FILTER),
    paint: {
      "text-halo-color": "#BBBBBB",
      "text-halo-width": 0.8,
    },
  });
};

const mapSetFilters = (map) => {
  //すさみ町対応
  map.setFilter("plateau_bldg", [
    "!",
    [
      "any",
      ["==", ["get", "filename"], "50352406_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352407_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352408_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352409_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352414_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352415_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352416_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352417_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352418_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352423_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352424_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352425_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352426_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352433_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352500_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352510_bldg_6697_op.gml"],
    ],
  ]);

  map.setFilter("plateau_bldg_fill", [
    "!",
    [
      "any",
      ["==", ["get", "filename"], "50352406_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352407_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352408_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352409_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352414_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352415_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352416_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352417_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352418_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352423_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352424_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352425_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352426_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352433_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352500_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352510_bldg_6697_op.gml"],
    ],
  ]);

  map.setFilter("plateau_bldg_fill_outline", [
    "!",
    [
      "any",
      ["==", ["get", "filename"], "50352406_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352407_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352408_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352409_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352414_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352415_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352416_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352417_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352418_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352423_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352424_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352425_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352426_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352433_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352500_bldg_6697_op.gml"],
      ["==", ["get", "filename"], "50352510_bldg_6697_op.gml"],
    ],
  ]);
};

function GsiPhotographMinZoom(map) {
  if (GetSIFScale(map) === 25000) {
    map.setLayerZoomRange("gsiphoto", 16, 24);
    map.setPaintProperty("gsiphoto", "raster-opacity", [
      "interpolate",
      ["linear"],
      ["zoom"],
      16,
      0,
      17,
      1,
    ]);
  } else {
    map.setLayerZoomRange("gsiphoto", 17, 24);
    map.setPaintProperty("gsiphoto", "raster-opacity", [
      "interpolate",
      ["linear"],
      ["zoom"],
      17,
      0,
      18,
      1,
    ]);
  }
}

function GetSIFScale(prm_map) {
  let ret = 10000;
  let bounds = prm_map.getBounds();
  let pos = [];
  pos.push(bounds.getSouthWest());
  pos.push(bounds.getSouthEast());
  pos.push(bounds.getNorthEast());
  pos.push(bounds.getNorthWest());
  for (let i = 0; i < pos.length; i++) {
    const lonlat = proj4(proj4.defs["EPSG:4326"], proj4.defs["EPSG:4301"], [
      pos[i].lng,
      pos[i].lat,
    ]);
    let X = lonlat[0] * 3600;
    let Y = lonlat[1] * 3600;
    let tmp = PosInMap(X, Y);

    if (!tmp) {
      ret = 25000;
    }
    return ret;
  }
}

// Terradraw用のStyle変更関数
export const transformStyleForTD = (previousStyle, nextStyle) => {
  // Terradrawで作成したソース、レイヤを引き継ぐ
  const tdSources = {};
  Object.entries(previousStyle.sources).forEach(([key, value]) => {
    if (key.startsWith("td-")) {
      tdSources[key] = value;
    }
  });
  const tdLayers = previousStyle.layers.filter((layer) =>
    layer.id.startsWith("td-")
  );

  return {
    ...nextStyle,
    sources: Object.assign(nextStyle.sources, tdSources),
    layers: nextStyle.layers.concat(tdLayers),
  };
};

// 購入履歴
export const getOrderHistoryMap = () => {
  return orderHistoryMap;
};
export const setOrderHistoryMap = (map) => {
  orderHistoryMap = map;
};
export const getIsOredrHistoryView = () => {
  return isOredrHistoryView;
};
export const setIsOredrHistoryView = (bool) => {
  isOredrHistoryView = bool;
};

// 購入履歴にあるデータのstateを変更する
export const setOrderHistoryState = (map) => {
  // 公共・任意の両ソースを対象にする（source-layer はいずれも hude）
  const targets = ["moj", "nini_kukaku"];
  const callFunction = () => {
    targets.forEach((source) => {
      if (!map.getSource(source)) return;
      // 現在表示されているタイル内のhude地物を取得
      const features = map.querySourceFeatures(source, {
        sourceLayer: "hude",
      });
      // 重複処理防止用（任意座標の id はローカル連番で公共と衝突しうるためソースごとに独立させる）
      const processedIds = new Set();

      // 地物をループし、キーを比較してfeature-stateを設定
      features.forEach((feature) => {
        if (feature.id != null && !processedIds.has(feature.id)) {
          processedIds.add(feature.id);
          // 地物から複合キーを生成
          const featureKey = createFeaturePropertyKey(feature.properties);
          const isPurchased =
            isOredrHistoryView && orderHistoryMap.has(featureKey);

          // 状態が変更されている場合のみsetFeatureStateを呼び出す
          const currentState = map.getFeatureState({
            source,
            sourceLayer: "hude",
            id: feature.id,
          });
          if (currentState.purchased !== isPurchased) {
            map.setFeatureState(
              { source, sourceLayer: "hude", id: feature.id },
              { purchased: isPurchased }
            );
          }
        }
      });
    });
  };
  // 地物などが読み込まれた後に実行
  if (map.getSource("moj") && map.isSourceLoaded("moj")) {
    callFunction();
  } else {
    map.once("idle", callFunction);
  }

};